import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from '@mui/material';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { exportStyledExcel } from '../../../utils/exportExcel';

const ABSENCE_TYPES = [
  {
    value: 'Permiso remunerado',
    label: 'Permiso remunerado',
    color: '#15803d',
    bg: '#dcfce7',
    deductsPayroll: false,
    description:
      'Para citas médicas o permisos autorizados que NO requieren descuento de nómina. Puede ser por horas o todo el día.',
  },
  {
    value: 'Permiso no remunerado',
    label: 'Permiso no remunerado',
    color: '#b45309',
    bg: '#fef3c7',
    deductsPayroll: true,
    description:
      'Permiso por horas (no mayor a 1 día hábil de trabajo / máx. 8 horas). Se descuenta de la nómina el tiempo que el trabajador está por fuera. Si se requiere más de un día corresponde a una Licencia no remunerada.',
  },
  {
    value: 'Licencia no remunerada',
    label: 'Licencia no remunerada',
    color: '#b91c1c',
    bg: '#fee2e2',
    deductsPayroll: true,
    description:
      'Licencia de mínimo 1 día completo. Si la ausencia requerida es de uno o más días completos, debe registrarse bajo esta modalidad.',
  },
  {
    value: 'Compensatorio por obras',
    label: 'Compensatorio por obras',
    color: '#1d4ed8',
    bg: '#dbeafe',
    deductsPayroll: false,
    description:
      'Tiempo compensatorio otorgado por trabajo en obras. Puede ser todo el día o por horas y NO genera descuento de nómina.',
  },
];

const getTypeMeta = (typeVal) =>
  ABSENCE_TYPES.find((t) => t.value === typeVal) || ABSENCE_TYPES[0];

const calcDaysBetween = (startStr, endStr) => {
  if (!startStr) return 1;
  const effEnd = endStr || startStr;
  const s = new Date(`${startStr}T00:00:00`);
  const e = new Date(`${effEnd}T00:00:00`);
  const diff = Math.round((e - s) / 86400000) + 1;
  return diff > 0 ? diff : 1;
};

const calcHoursFromRange = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const diffMin = eh * 60 + em - (sh * 60 + sm);
  return diffMin > 0 ? Number((diffMin / 60).toFixed(2)) : 0;
};

export default function AusentismoGeneral() {
  const company = sessionStorage.getItem('company') || 1;
  const todayStr = new Date().toLocaleDateString('en-CA');

  const [absences, setAbsences] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [filterEndDate, setFilterEndDate] = useState(todayStr);

  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    user_id: '',
    absence_type: 'Permiso remunerado',
    start_date: todayStr,
    end_date: todayStr,
    is_full_day: false,
    hours_absent: '2',
    start_time: '08:00',
    end_time: '10:00',
    hourly_value: '',
    deduction_amount: '',
    reason: '',
  });

  const fetchCatalogAndAbsences = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterUser) params.userId = filterUser;
      if (filterType) params.absenceType = filterType;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;

      const [absRes, usersRes] = await Promise.all([
        axios.get(`/absences/${company}`, { params }),
        axios.get(`/getUser/${company}`),
      ]);

      setAbsences(absRes.data?.data || []);
      const activeUsers = (usersRes.data?.data || []).filter(
        (u) => u.state !== false && u.state !== 0 && u.Rol?.name !== 'Administrador'
      );
      setUsers(activeUsers);
    } catch (error) {
      console.error('Error cargando novedades de ausentismo:', error);
    } finally {
      setLoading(false);
    }
  }, [company, filterUser, filterType, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchCatalogAndAbsences();
  }, [fetchCatalogAndAbsences]);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setForm({
      user_id: '',
      absence_type: 'Permiso remunerado',
      start_date: todayStr,
      end_date: todayStr,
      is_full_day: false,
      hours_absent: '2',
      start_time: '08:00',
      end_time: '10:00',
      hourly_value: '',
      deduction_amount: '',
      reason: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      user_id: item.user_id || '',
      absence_type: item.absence_type || 'Permiso remunerado',
      start_date: item.start_date || todayStr,
      end_date: item.end_date || item.start_date || todayStr,
      is_full_day: Boolean(item.is_full_day),
      hours_absent: String(item.hours_absent || ''),
      start_time: item.start_time ? String(item.start_time).slice(0, 5) : '08:00',
      end_time: item.end_time ? String(item.end_time).slice(0, 5) : '10:00',
      hourly_value: item.hourly_value ? String(item.hourly_value) : '',
      deduction_amount: item.deduction_amount ? String(item.deduction_amount) : '',
      reason: item.reason || '',
    });
    setDialogOpen(true);
  };

  const handleTypeChange = (newType) => {
    const isLicense = newType === 'Licencia no remunerada';
    const isUnpaidPerm = newType === 'Permiso no remunerado';
    const isCompensatory = newType === 'Compensatorio por obras';
    setForm((prev) => {
      let nextFullDay = prev.is_full_day;
      if (isLicense) {
        nextFullDay = true;
      } else if (isUnpaidPerm) {
        nextFullDay = false;
      } else if (isCompensatory) {
        nextFullDay = true;
      }
      const days = calcDaysBetween(prev.start_date, prev.end_date);
      const nextHours = nextFullDay
        ? String(days * 8)
        : prev.hours_absent && Number(prev.hours_absent) <= 8
        ? prev.hours_absent
        : '2';
      return {
        ...prev,
        absence_type: newType,
        is_full_day: nextFullDay,
        hours_absent: nextHours,
        end_date: isUnpaidPerm ? prev.start_date : prev.end_date,
      };
    });
  };

  const handleTimeRangeChange = (field, val) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: val };
      const computedHrs = calcHoursFromRange(updated.start_time, updated.end_time);
      if (computedHrs > 0) {
        updated.hours_absent = String(computedHrs);
      }
      return updated;
    });
  };

  const handleHoursChange = (val) => {
    setForm((prev) => ({
      ...prev,
      hours_absent: val,
    }));
  };

  const handleSave = async () => {
    if (!form.user_id) {
      Swal.fire('Atención', 'Selecciona el empleado.', 'warning');
      return;
    }
    if (!form.start_date) {
      Swal.fire('Atención', 'Selecciona la fecha de la novedad.', 'warning');
      return;
    }

    const isLicense = form.absence_type === 'Licencia no remunerada';
    const isUnpaidPerm = form.absence_type === 'Permiso no remunerado';
    const effFullDay = isLicense ? true : isUnpaidPerm ? false : Boolean(form.is_full_day);
    const effEndDate = effFullDay ? form.end_date || form.start_date : form.start_date;

    if (effEndDate < form.start_date) {
      Swal.fire('Atención', 'La fecha final no puede ser menor a la fecha inicial.', 'warning');
      return;
    }

    const daysCount = calcDaysBetween(form.start_date, effEndDate);
    if (isLicense && daysCount < 1) {
      Swal.fire('Atención', 'La licencia no remunerada debe ser de mínimo 1 día.', 'warning');
      return;
    }

    const hrsAbsent = effFullDay ? daysCount * 8 : Number(form.hours_absent || 0);
    if (!effFullDay && hrsAbsent <= 0) {
      Swal.fire(
        'Atención',
        'Indica cuántas horas estuvo por fuera el trabajador.',
        'warning'
      );
      return;
    }

    if (isUnpaidPerm && (hrsAbsent > 8 || daysCount > 1)) {
      Swal.fire(
        'Atención',
        'El permiso no remunerado no puede ser mayor a un día hábil de trabajo (máximo 8 horas). Si se requiere más de un día, debe registrarse como Licencia no remunerada.',
        'warning'
      );
      return;
    }

    if (!form.reason.trim()) {
      Swal.fire(
        'Atención',
        'Ingresa la justificación o motivo de la novedad (ej. Cita médica, permiso personal, compensatorio obra).',
        'warning'
      );
      return;
    }

    try {
      setSaving(true);
      const payload = {
        user_id: Number(form.user_id),
        company: Number(company),
        absence_type: form.absence_type,
        start_date: form.start_date,
        end_date: effEndDate,
        is_full_day: effFullDay ? 1 : 0,
        hours_absent: hrsAbsent,
        start_time: effFullDay ? null : form.start_time || null,
        end_time: effFullDay ? null : form.end_time || null,
        hourly_value: 0,
        deduction_amount: 0,
        reason: form.reason.trim(),
      };

      if (editingItem) {
        await axios.put(`/updateAbsence/${editingItem.id}`, payload);
      } else {
        await axios.post('/saveAbsence', payload);
      }

      setDialogOpen(false);
      await fetchCatalogAndAbsences();
      Swal.fire({
        icon: 'success',
        title: editingItem ? 'Novedad actualizada' : 'Novedad registrada',
        text: 'La novedad se cruza automáticamente con el Reporte de Horas Extra.',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error guardando novedad:', error);
      Swal.fire(
        'Error',
        error.response?.data?.message || 'No se pudo guardar la novedad de ausentismo.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar novedad de ausentismo?',
      text: `Se eliminará el registro de ${item.User?.name || 'empleado'} (${item.absence_type}).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Sí, eliminar',
    });
    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`/deleteAbsence/${item.id}`);
      await fetchCatalogAndAbsences();
      Swal.fire({
        icon: 'success',
        title: 'Novedad eliminada',
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', 'No se pudo eliminar la novedad.', 'error');
    }
  };

  const stats = useMemo(() => {
    const total = absences.length;
    const withDeduction = absences.filter((a) => Boolean(a.deducts_payroll)).length;
    const withoutDeduction = total - withDeduction;
    const totalHours = absences.reduce((acc, a) => acc + Number(a.hours_absent || 0), 0);
    const withDeductionHours = absences
      .filter((a) => Boolean(a.deducts_payroll))
      .reduce((acc, a) => acc + Number(a.hours_absent || 0), 0);
    return { total, withDeduction, withoutDeduction, totalHours, withDeductionHours };
  }, [absences]);

  const handleExportExcel = () => {
    if (absences.length === 0) return;
    const headers = [
      'Empleado',
      'Rol',
      'Tipo de Novedad',
      'Fecha Inicio',
      'Fecha Fin',
      'Modalidad',
      'Horas Fuera',
      'Descuenta Nómina',
      'Valor Descuento ($)',
      'Justificación / Motivo',
    ];
    const data = absences.map((a) => ({
      Empleado: a.User?.name || '—',
      Rol: a.User?.Rol?.name || '—',
      'Tipo de Novedad': a.absence_type,
      'Fecha Inicio': a.start_date,
      'Fecha Fin': a.end_date,
      Modalidad: a.is_full_day
        ? `Todo el día (${a.days_count || 1} día(s))`
        : `Por horas (${a.start_time ? a.start_time.slice(0, 5) : ''} - ${
            a.end_time ? a.end_time.slice(0, 5) : ''
          })`,
      'Horas Fuera': Number(a.hours_absent || 0),
      'Descuenta Nómina': a.deducts_payroll ? 'SÍ' : 'NO',
      'Valor Descuento ($)': Number(a.deduction_amount || 0),
      'Justificación / Motivo': a.reason || '—',
    }));

    exportStyledExcel({
      filename: `reporte_ausentismo_novedades_${filterStartDate}_al_${filterEndDate}.xlsx`,
      sheetName: 'Ausentismo y Novedades',
      headers,
      data,
    });
  };

  const currentTypeMeta = getTypeMeta(form.absence_type);
  const isLicenseSelected = form.absence_type === 'Licencia no remunerada';
  const isUnpaidPermissionSelected = form.absence_type === 'Permiso no remunerado';
  const requiresPayrollDeduction =
    form.absence_type === 'Permiso no remunerado' ||
    form.absence_type === 'Licencia no remunerada';

  return (
    <Box>
      {/* Encabezado */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Módulo de Ausentismo General (Novedades)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registra permisos remunerados, permisos no remunerados, licencias no remuneradas y compensatorios por obras. Estas novedades se cruzan automáticamente con el Reporte de Horas Extra.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="success"
            startIcon={<TableCellsIcon className="h-5 w-5" />}
            disabled={absences.length === 0}
            onClick={handleExportExcel}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Exportar Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<PlusIcon className="h-5 w-5" />}
            onClick={handleOpenCreate}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Registrar Novedad
          </Button>
        </Stack>
      </Stack>

      {/* Tarjetas de resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              TOTAL NOVEDADES
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              {stats.total}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #bbf7d0', bgcolor: '#f0fdf4' }}>
            <Typography variant="caption" color="#15803d" fontWeight={700}>
              SIN DESCUENTO (REMUNERADO / COMP.)
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#15803d">
              {stats.withoutDeduction}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
            <Typography variant="caption" color="#b45309" fontWeight={700}>
              CON DESCUENTO DE NÓMINA
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#b45309">
              {stats.withDeduction} ({stats.totalHours}h totales)
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #fecaca', bgcolor: '#fef2f2' }}>
            <Typography variant="caption" color="#b91c1c" fontWeight={700}>
              TOTAL HORAS A DESCONTAR
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#b91c1c">
              {stats.withDeductionHours}h
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Empleado</InputLabel>
              <Select
                label="Empleado"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
              >
                <MenuItem value="">Todos los empleados</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Novedad</InputLabel>
              <Select
                label="Tipo de Novedad"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">Todos los tipos</MenuItem>
                {ABSENCE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2} md={2.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={2.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setFilterUser('');
                setFilterType('');
              }}
              sx={{ textTransform: 'none', borderRadius: 2, height: 40 }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : absences.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No hay novedades de ausentismo registradas en este período.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Empleado</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tipo de Novedad</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fecha(s)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tiempo Fuera</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nómina / Descuento</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Justificación / Motivo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {absences.map((item) => {
                const meta = getTypeMeta(item.absence_type);
                return (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{item.User?.name || '—'}</TableCell>
                    <TableCell>{item.User?.Rol?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.absence_type}
                        size="small"
                        sx={{
                          bgcolor: meta.bg,
                          color: meta.color,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.start_date === item.end_date
                        ? item.start_date
                        : `${item.start_date} al ${item.end_date}`}
                    </TableCell>
                    <TableCell>
                      {item.is_full_day ? (
                        <Chip
                          size="small"
                          label={`Todo el día (${item.days_count || 1} día${
                            (item.days_count || 1) > 1 ? 's' : ''
                          })`}
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {Number(item.hours_absent)}h fuera
                          {item.start_time && item.end_time
                            ? ` (${String(item.start_time).slice(0, 5)} - ${String(
                                item.end_time
                              ).slice(0, 5)})`
                            : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.deducts_payroll ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label={
                            Number(item.deduction_amount) > 0
                              ? `Descuenta $${Number(item.deduction_amount).toLocaleString('es-CO')}`
                              : `Descuenta (${Number(item.hours_absent)}h)`
                          }
                          sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label="Sin descuento"
                          sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={item.reason || ''}>
                        <Typography variant="body2" noWrap>
                          {item.reason || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEdit(item)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(item)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Modal Crear / Editar Novedad */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editingItem ? 'Editar Novedad de Ausentismo' : 'Registrar Novedad de Ausentismo'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <FormControl fullWidth required>
              <InputLabel>Empleado *</InputLabel>
              <Select
                label="Empleado *"
                value={form.user_id}
                onChange={(e) => setForm((prev) => ({ ...prev, user_id: e.target.value }))}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name} ({u.Rol?.name || 'Personal'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Tipo de Novedad / Ausentismo *</InputLabel>
              <Select
                label="Tipo de Novedad / Ausentismo *"
                value={form.absence_type}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                {ABSENCE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert
              severity={requiresPayrollDeduction ? 'warning' : 'success'}
              sx={{ borderRadius: 2 }}
            >
              <Typography variant="caption" fontWeight={700} display="block">
                {currentTypeMeta.label}:
              </Typography>
              <Typography variant="caption">{currentTypeMeta.description}</Typography>
            </Alert>

            {/* Modalidad: Oculta para Licencia (siempre mínimo 1 día) y para Permiso no remunerado (siempre por horas, máx 1 día hábil) */}
            {!isLicenseSelected && !isUnpaidPermissionSelected && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  MODALIDAD DEL PERMISO
                </Typography>
                <RadioGroup
                  row
                  value={form.is_full_day ? 'full' : 'hours'}
                  onChange={(e) => {
                    const isFull = e.target.value === 'full';
                    setForm((prev) => ({
                      ...prev,
                      is_full_day: isFull,
                      end_date: isFull ? prev.end_date || prev.start_date : prev.start_date,
                      hours_absent: isFull
                        ? String(calcDaysBetween(prev.start_date, prev.end_date) * 8)
                        : '2',
                    }));
                  }}
                >
                  <FormControlLabel
                    value="hours"
                    control={<Radio size="small" />}
                    label="Por horas (parcial)"
                  />
                  <FormControlLabel
                    value="full"
                    control={<Radio size="small" />}
                    label="Permiso todo el día"
                  />
                </RadioGroup>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={form.is_full_day || isLicenseSelected ? 6 : 12}>
                <TextField
                  fullWidth
                  type="date"
                  label={
                    form.is_full_day || isLicenseSelected
                      ? 'Fecha Inicio *'
                      : 'Fecha del Permiso *'
                  }
                  InputLabelProps={{ shrink: true }}
                  value={form.start_date}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      start_date: val,
                      end_date: prev.end_date < val ? val : prev.end_date,
                    }));
                  }}
                />
              </Grid>
              {(form.is_full_day || isLicenseSelected) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha Fin (mínimo 1 día) *"
                    InputLabelProps={{ shrink: true }}
                    value={form.end_date}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        end_date: e.target.value,
                      }))
                    }
                  />
                </Grid>
              )}
            </Grid>

            {/* Campos de horas cuando es permiso por horas */}
            {!form.is_full_day && !isLicenseSelected && (
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Hora Salida"
                    InputLabelProps={{ shrink: true }}
                    value={form.start_time}
                    onChange={(e) => handleTimeRangeChange('start_time', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Hora Regreso"
                    InputLabelProps={{ shrink: true }}
                    value={form.end_time}
                    onChange={(e) => handleTimeRangeChange('end_time', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{
                      step: '0.5',
                      min: '0.5',
                      max: isUnpaidPermissionSelected ? '8' : '24',
                    }}
                    label={
                      isUnpaidPermissionSelected
                        ? 'Horas por fuera (máx. 8h) *'
                        : 'Horas por fuera *'
                    }
                    helperText={
                      isUnpaidPermissionSelected
                        ? 'Permiso por horas, no mayor a 1 día hábil'
                        : ''
                    }
                    value={form.hours_absent}
                    onChange={(e) => handleHoursChange(e.target.value)}
                  />
                </Grid>
              </Grid>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              required
              label="Justificación / Motivo de la Novedad *"
              placeholder={
                form.absence_type === 'Permiso remunerado'
                  ? 'Ej: Cita médica general en EPS / Permiso autorizado sin descuento...'
                  : form.absence_type === 'Compensatorio por obras'
                  ? 'Ej: Día compensatorio por trabajo en obra proyecto X...'
                  : 'Ej: Diligencia personal solicitada por el empleado...'
              }
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            {saving ? 'Guardando...' : 'Guardar Novedad'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
